/* ═══════════════════════════════════════════════════════════════
   PiARIS — Watchtower Showcase
   Indigo → Cyan gradient. Animated radar sweep, particles, capability
   pills, feature cards with stat counters, and a live scan preview.
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { P } from "../constants";
import { PRODUCT_GRADIENTS } from "./gradients";
import { ProductLockup } from "./ProductLockup";

var E = React.createElement;
var G = PRODUCT_GRADIENTS.watchtower;

/** Animated radar sweep SVG — single rotating beam with grid + center dot.
    Respects prefers-reduced-motion via global CSS. */
function RadarSweep(p) {
  var sz = p.size || 140;
  return E("svg", {
    width: sz,
    height: sz,
    viewBox: "0 0 140 140",
    "aria-hidden": "true",
    className: "wt-radar",
    style: { filter: "drop-shadow(0 4px 16px rgba(255,255,255,0.2))" }
  },
    /* Grid rings */
    [55, 42, 28, 14].map(function(r, i) {
      return E("circle", {
        key: i,
        cx: 70, cy: 70, r: r,
        fill: "none",
        stroke: "rgba(255,255,255,0.3)",
        strokeWidth: 1
      });
    }),
    /* Crosshairs */
    E("line", { x1: 70, y1: 15, x2: 70, y2: 125, stroke: "rgba(255,255,255,0.2)", strokeWidth: 1 }),
    E("line", { x1: 15, y1: 70, x2: 125, y2: 70, stroke: "rgba(255,255,255,0.2)", strokeWidth: 1 }),
    /* Sweep gradient */
    E("defs", null,
      E("linearGradient", { id: "wt-sweep", x1: "0%", y1: "0%", x2: "100%", y2: "100%" },
        E("stop", { offset: "0%", stopColor: "#67E8F9", stopOpacity: 0.9 }),
        E("stop", { offset: "100%", stopColor: "#67E8F9", stopOpacity: 0 })
      )
    ),
    /* Rotating beam — wedge */
    E("g", { className: "wt-radar-sweep", style: { transformOrigin: "70px 70px" } },
      E("path", {
        d: "M70 70 L70 15 A55 55 0 0 1 109 31 Z",
        fill: "url(#wt-sweep)"
      })
    ),
    /* Center dot */
    E("circle", { cx: 70, cy: 70, r: 4, fill: "#67E8F9" }),
    E("circle", { cx: 70, cy: 70, r: 8, fill: "none", stroke: "#67E8F9", strokeWidth: 1, opacity: 0.5 })
  );
}

/** Floating particles overlay — decorative dots drifting upward */
function Particles() {
  var dots = [
    { l: 12, t: 80, d: 0,    s: 6 },
    { l: 22, t: 65, d: 1.2,  s: 4 },
    { l: 38, t: 90, d: 0.4,  s: 5 },
    { l: 55, t: 70, d: 2.0,  s: 4 },
    { l: 68, t: 85, d: 0.8,  s: 6 },
    { l: 82, t: 60, d: 1.6,  s: 4 },
    { l: 92, t: 78, d: 0.6,  s: 5 }
  ];
  return E("div", {
    "aria-hidden": "true",
    style: {
      position: "absolute", inset: 0, overflow: "hidden",
      pointerEvents: "none"
    }
  },
    dots.map(function(d, i) {
      return E("div", {
        key: i,
        className: "wt-particle",
        style: {
          position: "absolute",
          left: d.l + "%",
          top: d.t + "%",
          width: d.s,
          height: d.s,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.5)",
          animationDelay: d.d + "s"
        }
      });
    })
  );
}

/** Live scan preview mockup — shows fake "Crawling..." progress with stats */
function ScanPreview() {
  return E("figure", {
    "aria-label": "Live scan preview mockup",
    style: {
      background: "rgba(15,23,42,0.55)",
      border: "1px solid rgba(255,255,255,0.18)",
      borderRadius: 12,
      padding: 14,
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      marginTop: 0
    }
  },
    E("div", {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 10
      }
    },
      E("div", { style: { display: "flex", alignItems: "center", gap: 6 } },
        E("span", {
          "aria-hidden": "true",
          className: "wt-pulse-dot",
          style: {
            width: 8, height: 8, borderRadius: "50%",
            background: "#67E8F9",
            boxShadow: "0 0 8px #67E8F9"
          }
        }),
        E("span", {
          style: { fontSize: 11, fontWeight: 700, color: "#fff" }
        }, "Crawling \u2014 nyc.gov")
      ),
      E("span", {
        style: { fontSize: 10, color: "rgba(255,255,255,0.6)", fontFamily: "monospace" }
      }, "00:42")
    ),
    /* Progress bar */
    E("div", {
      "aria-hidden": "true",
      style: {
        height: 5, borderRadius: 3,
        background: "rgba(255,255,255,0.15)",
        overflow: "hidden",
        marginBottom: 12
      }
    },
      E("div", {
        className: "wt-progress",
        style: {
          height: "100%",
          width: "62%",
          background: "linear-gradient(90deg, #4F46E5, #67E8F9)",
          borderRadius: 3
        }
      })
    ),
    /* Stat row */
    E("dl", {
      style: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 10,
        margin: 0
      }
    },
      [
        { k: "Pages", v: "847" },
        { k: "Issues", v: "12.4k" },
        { k: "Critical", v: "318" }
      ].map(function(s, i) {
        return E("div", {
          key: i,
          style: {
            background: "rgba(255,255,255,0.08)",
            borderRadius: 8,
            padding: "8px 10px",
            textAlign: "center"
          }
        },
          E("dt", {
            style: {
              fontSize: 8,
              fontWeight: 700,
              color: "rgba(255,255,255,0.6)",
              textTransform: "uppercase",
              letterSpacing: ".06em",
              marginBottom: 2
            }
          }, s.k),
          E("dd", {
            style: {
              fontSize: 16,
              fontWeight: 800,
              color: "#fff",
              margin: 0,
              fontFeatureSettings: '"tnum"'
            }
          }, s.v)
        );
      })
    )
  );
}

/** WatchtowerShowcase — the full marketing block. */
function WatchtowerShowcase(p) {
  var ctaLabel = p.ctaLabel || "Open Watchtower";
  return E("section", {
    role: "region",
    "aria-labelledby": "wt-showcase-title",
    style: {
      borderRadius: 18,
      overflow: "hidden",
      border: "2px solid " + G.border + "30",
      background: G.hero,
      color: "#FFFFFF",
      position: "relative",
      boxShadow: "0 10px 40px " + G.glow
    }
  },
    E(Particles, null),
    /* Header band */
    E("div", {
      style: {
        position: "relative",
        zIndex: 1,
        padding: "28px 28px 20px",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 20,
        alignItems: "center"
      },
      className: "wt-header"
    },
      E("div", null,
        E("div", {
          style: {
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10
          }
        },
          E(ProductLockup, { size: "small", white: true }),
          E("span", { "aria-hidden": "true", style: { color: "rgba(255,255,255,0.4)" } }, "/"),
          E("span", {
            style: {
              fontSize: 12,
              fontWeight: 700,
              color: "rgba(255,255,255,0.85)",
              textTransform: "uppercase",
              letterSpacing: ".08em"
            }
          }, "Watchtower")
        ),
        E("h2", {
          id: "wt-showcase-title",
          style: {
            fontSize: 26,
            fontWeight: 900,
            margin: 0,
            lineHeight: 1.2,
            letterSpacing: "-0.4px"
          }
        }, "Full-Domain Accessibility Surveillance"),
        E("p", {
          style: {
            fontSize: 13,
            color: "rgba(255,255,255,0.82)",
            margin: "8px 0 0",
            lineHeight: 1.55,
            maxWidth: 520
          }
        }, "Crawl every page and every linked PDF. Dual-engine audit with axe-core + HTML_CodeSniffer. Live DOM capture, AI remediation, weighted compliance scoring."),
        /* Capability pills */
        E("ul", {
          style: {
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            listStyle: "none",
            padding: 0,
            margin: "14px 0 0"
          }
        },
          ["WCAG 2.2 AA", "Section 508", "PDF/UA", "ADA Title II", "EAA EN 301 549"].map(function(c) {
            return E("li", {
              key: c,
              style: {
                padding: "4px 12px",
                borderRadius: 14,
                background: G.pillBg,
                border: "1px solid " + G.pillBorder,
                fontSize: 10,
                fontWeight: 700,
                color: "#fff"
              }
            }, c);
          })
        )
      ),
      /* Radar */
      E("div", { className: "wt-radar-wrap", style: { flexShrink: 0 } },
        E(RadarSweep, { size: 140 })
      )
    ),
    /* Body grid: 3 feature cards + scan preview */
    E("div", {
      style: {
        position: "relative",
        zIndex: 1,
        padding: "0 28px 28px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 16
      },
      className: "wt-body"
    },
      /* Feature stat cards (left) */
      E("ul", {
        style: {
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 10,
          listStyle: "none",
          padding: 0,
          margin: 0,
          gridColumn: "1 / 2"
        }
      },
        [
          { v: "10", l: "Max Crawl Depth", k: "depth" },
          { v: "2\u00D7", l: "Audit Engines",  k: "engines" },
          { v: "7",  l: "Report Types",    k: "reports" }
        ].map(function(s) {
          return E("li", {
            key: s.k,
            style: {
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 10,
              padding: "12px 10px",
              textAlign: "center"
            }
          },
            E("div", {
              style: {
                fontSize: 22,
                fontWeight: 900,
                color: "#fff",
                lineHeight: 1
              }
            }, s.v),
            E("div", {
              style: {
                fontSize: 9,
                fontWeight: 700,
                color: "rgba(255,255,255,0.7)",
                textTransform: "uppercase",
                letterSpacing: ".04em",
                marginTop: 4
              }
            }, s.l)
          );
        })
      ),
      /* Scan preview (right) */
      E("div", { style: { gridColumn: "2 / 3" } }, E(ScanPreview, null)),
      /* CTA — full width below */
      E("div", {
        style: {
          gridColumn: "1 / -1",
          marginTop: 4,
          display: "flex",
          gap: 10,
          flexWrap: "wrap"
        }
      },
        p.onCta ? E("button", {
          onClick: p.onCta,
          style: {
            minHeight: 44,
            padding: "10px 22px",
            borderRadius: 10,
            border: "none",
            background: "#FFFFFF",
            color: G.pri,
            fontSize: 13,
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)"
          }
        }, "\uD83C\uDF10 " + ctaLabel) : null,
        p.onSecondary ? E("button", {
          onClick: p.onSecondary,
          style: {
            minHeight: 44,
            padding: "10px 22px",
            borderRadius: 10,
            border: "1.5px solid rgba(255,255,255,0.4)",
            background: "rgba(255,255,255,0.08)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer"
          }
        }, p.secondaryLabel || "Import Scan") : null
      )
    )
  );
}

export { WatchtowerShowcase };
